import { ListHeading } from "@/components/shared/list-header";
import {
  Input,
  View,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui";
import { ClipboardText } from "@/components/shared/icons";
import { Typography } from "@/components/shared/typography";
import { InputFormControl } from "@/components/shared/input-form-control";
import {
  type Control,
  Controller,
  useController,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { Divider } from "@/components/shared/divider";
import { ListCheckboxInputItem } from "@/components/shared/list-checkbox-input";
import { type BabySitterFormData } from "./useBabyProfileForm";
import {
  babysitterTasksOptions,
  babySitterWADescription,
  healthConditionOptions,
} from "./lib";
import { FormControlError } from "@/components/shared/form-control-error";
import { SalesModal } from "@/components/shared/booking/sales-modal";
import { useState } from "react";

export const BabySitterForm = ({
  control,
}: {
  control: Control<BabySitterFormData>;
}) => {
  return (
    <View>
      <ListHeading title="Child Profile" icon={ClipboardText} />
      <Typography
        color="foreground-intermediate"
        variant="body-md"
        className="pt-1"
      >
        Helps us share your child's needs and assign the right babysitter.
      </Typography>
      <View className="pt-4">
        <Divider className="-mx-4 my-4 border-b-2 md:mx-0" />

        {/* Save Profile As Dropdown */}
        <View className="pb-4">
          <Typography variant="label-sm" className="block pb-2">
            Save this profile as*
          </Typography>
          <Controller
            control={control}
            name="saveAs"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select profile type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem label="a" value="Parent/Guardian">
                    Parent/Guardian
                  </SelectItem>
                  <SelectItem label="b" value="Other">
                    Other
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </View>

        {/* Name and Age */}
        <View className="flex flex-col gap-2 md:flex-row">
          <View className="flex-1">
            <InputFormControl
              name="name"
              label="Child's Name*"
              component={Input}
              control={control}
              componentProps={{
                placeholder: "Enter the baby's name",
              }}
            />
          </View>
          <View className="flex-1">
            <InputFormControl
              name="age"
              label="Age*"
              component={Input}
              control={control}
              componentProps={{
                placeholder: "Enter the age",
              }}
            />
          </View>
        </View>

        <Divider className="-mx-4 my-4 border-b-2 md:mx-0" />

        {/* Health Conditions Checkboxes */}
        <HealthConditionsCheckbox control={control} />

        <Divider className="-mx-4 my-4 border-b-2 md:mx-0" />

        {/* Babysitter Tasks */}
        <BabySitterTasksCheckbox control={control} />

        <Divider className="-mx-4 my-4 border-b-2 md:mx-0" />

        {/* Routine Details */}
        <View>
          <Typography variant="label-sm" className="block pb-2">
            Tell us a little more about your child's needs or routine*
          </Typography>
          <InputFormControl
            name="routineDetails"
            component={Textarea}
            control={control}
            componentProps={{
              placeholder: "e.g., allergy: peanuts, nap 2–3 pm",
            }}
          />
        </View>
      </View>
    </View>
  );
};

const HealthConditionsCheckbox = ({
  control,
  disabled = false,
}: {
  control: Control<BabySitterFormData>;
  disabled?: boolean;
}) => {
  const [openSalesModal, setOpenSalesModal] = useState(false);
  const healthConditions = useWatch({
    control,
    name: "healthConditions",
  });

  const { fieldState } = useController({
    control,
    name: "healthConditions",
  });

  const hasAnyConditionSelected = healthConditions
    ? Object.values(healthConditions).some((value) => value === true)
    : false;

  return (
    <View>
      <Typography variant="label-sm" className="block pb-1">
        Does your child currently display any of the following?
      </Typography>
      <Typography
        variant="body-sm"
        color="foreground-intermediate"
        className="block pb-4"
      >
        Select all that apply
      </Typography>

      {healthConditionOptions.map((option) => (
        <Controller
          key={option.key}
          control={control}
          name={`healthConditions.${option.key as keyof BabySitterFormData["healthConditions"]}`}
          render={({ field }) => (
            <ListCheckboxInputItem
              checked={field.value ?? false}
              onChange={(_, value) => {
                field.onChange(value);
              }}
              label={option.label}
              disabled={disabled}
            />
          )}
        />
      ))}

      <Controller
        control={control}
        name="healthConditions"
        render={() => (
          <ListCheckboxInputItem
            checked={false}
            onChange={() => setOpenSalesModal(true)}
            label="Select All"
            disabled={disabled}
          />
        )}
      />

      <FormControlError error={fieldState.error?.message} />

      {/* Conditional Details Textarea */}
      {hasAnyConditionSelected && (
        <View className="pt-4">
          <Typography variant="label-sm" className="block pb-2">
            Please provide details about the conditions*
          </Typography>
          <Typography
            variant="body-sm"
            color="foreground-intermediate"
            className="block pb-2"
          >
            Describe the care requirements
          </Typography>
          <InputFormControl
            name="healthConditionsDetails"
            component={Textarea}
            control={control}
            componentProps={{
              placeholder: "e.g., Allergic to peanuts, requires EpiPen nearby",
            }}
          />
        </View>
      )}
      <SalesModal
        open={openSalesModal}
        setOpen={() => setOpenSalesModal(false)}
        title="Need help with your child's care requirements?"
        description={babySitterWADescription}
      />
    </View>
  );
};

const BabySitterTasksCheckbox = ({
  control,
  disabled = false,
}: {
  control: Control<BabySitterFormData>;
  disabled?: boolean;
}) => {
  const form = useFormContext<BabySitterFormData>();

  const babysitterTasks = useWatch({
    control,
    name: "babysitter_tasks",
  });

  const { fieldState } = useController({
    control,
    name: "babysitter_tasks",
  });

  const allTasksSelected = babysitterTasks
    ? Object.values(babysitterTasks).every((value) => value === true)
    : false;

  const handleSelectAll = () => {
    const newValue = !allTasksSelected;
    const updatedTasks = { ...babysitterTasks };

    babysitterTasksOptions.forEach((option) => {
      updatedTasks[option.key as keyof BabySitterFormData["babysitter_tasks"]] =
        newValue;
    });

    form.setValue("babysitter_tasks", updatedTasks, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    form.trigger(["babysitter_tasks"]);
  };

  return (
    <View>
      <Typography variant="label-sm" className="block pb-1">
        What assistance does your child require from our babysitters?*
      </Typography>
      <Typography
        variant="body-sm"
        color="foreground-intermediate"
        className="block pb-4"
      >
        Select all that apply
      </Typography>

      {babysitterTasksOptions.map((option) => (
        <Controller
          key={option.key}
          control={control}
          name={`babysitter_tasks.${option.key as keyof BabySitterFormData["babysitter_tasks"]}`}
          render={({ field }) => (
            <ListCheckboxInputItem
              checked={field.value ?? false}
              onChange={(_, value) => {
                field.onChange(value);
              }}
              label={option.label}
              caption={option.caption}
              disabled={disabled}
            />
          )}
        />
      ))}

      <ListCheckboxInputItem
        checked={allTasksSelected}
        onChange={handleSelectAll}
        label="Select All"
        disabled={disabled}
      />
      <FormControlError error={fieldState.error?.message} />
    </View>
  );
};
